// @flow
import * as React from 'react';
import classnames from 'classnames';
import { get, noop, values, pick } from 'lodash';

import { withRouter } from 'Client/router/withRouter';
import { convertLocationToURI } from 'Utils/uri/uriUtils';
import { DEFAULT_NO_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { setDuration } from 'Store/trip-criteria/duration/duration.action';
import { setDepartureCity } from 'Store/trip-criteria/departure-city/departureCity.action';
import { setKids, resetKids } from 'Store/trip-information/kids/kids.action';
import { setAdultsNumber } from 'Store/trip-information/adults/adults.action';
import { WatchLocationQueryChange } from 'Components/utils/watch-location-query-change/WatchLocationQueryChange';
import { getFormattedKidsBirthdate } from 'Utils/trip-information/tripInformationUtils';
import redirectUtils from 'Utils/uri/redirectUtils';
import { QUERY_DURATION, QUERY_TRANSPORT } from 'Utils/uri/constants';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import ReactAppContext from 'Context/ReactAppContext';
import ParticipantsValidator, {
  type FormErrorsType,
} from 'Client/cms/toolkit/product-booking/participants.validator';
import { GridCell } from 'Components/frameworks/grid-cell/Grid.cell.jsx';
import { GridRow } from 'Components/frameworks/grid-row/Grid.row.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import { Button } from 'Components/frameworks/button/Button.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import Duration from 'Components/widgets/trip-information/duration/Duration.jsx';
import { BookingFormBlockField as BlockField } from 'Components/widgets/booking-form/block-field/BlockField.jsx';
import Participants from 'Components/widgets/trip-information/participants/Participants.jsx';
import { DepartureCities } from 'Components/widgets/trip-information/departure-city/DepartureCities.jsx';
import { Notification } from 'Components/frameworks/notification/Notification.jsx';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { isResort } from 'Components/utils/product/product.utils';
import {
  DEFAULT_MIN_AGE,
  DEFAULT_MAX_AGE,
} from 'Components/widgets/trip-information/participants/participants.constants';
import ParticipantsErrors from 'Client/cms/toolkit/product-booking/participantsErrors.jsx';

const COLS_M3 = { M: 3 };

type Props = {
  adultOnly?: boolean,
  isWinterResort: boolean,
  kids: Array<*>,
  adultsNumber: number,
  duration: number,
  departureDate?: string,
  isDepartureDateAvailaible?: boolean,
  departureCity: string,
  productId: string,
  children?: React.Node,
  childAge: number,
  // this is duplicated from i18n. But to add the <ParticipantsErrors /> here, it's simpler to do so
  // and we should not use a recomposition for the i18n anyway
  config: CMSContent,
  isJoyView?: boolean,
  isTransportEnabled?: boolean,
  pushImage?: string,
  responsive: ResponsiveProps,
  setKids: typeof setKids,
  resetKids: typeof resetKids,
  setAdultsNumber: typeof setAdultsNumber,
  setDuration: typeof setDuration,
  setDepartureCity: typeof setDepartureCity,
  legacyResortRedirect: () => ?string,
  goToLegacyBookingEngine: () => void,
  getPackages: (arrivalDate?: ?string) => void,
  dataSelector: string,
  resort: Resort,
  i18n: {
    adultAge?: number,
    checkPriceLabel?: string,
    bookingCriteria?: {
      initialDuration?: number,
      durations?: Array<number>,
      departureCities?: Array<{
        name: string,
        value: string,
      }>,
    },
    didYouKnowLabel: string,
    kidStayIncludedDescription: string,
    kidStayIncludedLabel: string,
    childrenBirthdayDateFormatPlaceholder?: string,
    dateFormat?: string,
    dayPlaceholder?: string,
    monthPlaceholder?: string,
    yearPlaceholder?: string,
    dateValidate?: string,
    selectAdultsLabels?: *,
    selectChildrenLabels?: *,
    legalNotice?: string,
    minAgeInformation?: string,
    adultAgeErrorMessage?: string,
    minAgeErrorMessage?: string,
    checkPriceLabel?: string,
    transfer?: {
      transferFrom: string,
      noTransfer: string,
      noTransferAvailable: string,
    },
    transport?: {
      title?: string,
      description?: string,
      linkDescription?: string,
      visible?: boolean,
    },
    adultAge?: number,
    proposalsLoaderLabel?: string,
    skiPassIncludedLabel?: string,
    skiPassIncludedDescription?: string,
  },
  tripInformation: TripInformation,
  history: RouterHistory,
  location: RouterLocation<
    {},
    {
      [QUERY_TRANSPORT | QUERY_DURATION]: string,
    },
  >,
};

class BookingCriteriaComponent extends React.Component<Props, *> {
  static contextType = ReactAppContext;
  static defaultProps = {
    i18n: {},
    isWinterResort: false,
    adultsNumber: 2,
    childAge: 0,
    isJoyView: false,
    isTransportEnabled: false,
    kids: [],
    departureCity: '',
    duration: 7,
    productId: '',
    // $FlowFixMe
    setKids: noop,
    // $FlowFixMe
    setAdultsNumber: noop,
    setDuration: noop,
    // $FlowFixMe
    setDepartureCity: noop,
    legacyResortRedirect: noop,
    goToLegacyBookingEngine: noop,
    dataSelector: 'BookingCriteria',
  };

  state = {
    isLoading: false,
    hasCheckedPrice: false,
  };

  componentDidMount() {
    const {
      adultOnly,
      tripInformation: { kids },
    } = this.props;
    if (adultOnly && kids.length) {
      this.props.resetKids();
    }
  }

  checkFormErrors(): FormErrorsType {
    const dateFormat = get(this.props, 'i18n.dateFormat', 'DD/MM/YYYY');
    const birthdays = getFormattedKidsBirthdate(this.props.kids, dateFormat);
    const adultAgeInYear = +get(this.props, 'i18n.adultAge', DEFAULT_MAX_AGE);
    const childAge =
      typeof this.props.childAge === 'number' ? this.props.childAge : DEFAULT_MIN_AGE;
    const childAgeInYear = childAge / 12;

    const { adultsNumber = 0, kids, departureDate = '' } = this.props;

    return ParticipantsValidator.checkFormErrors({
      adults: adultsNumber,
      children: kids.length,
      departureDate,
      birthdays,
      dateFormat,
      adultAge: adultAgeInYear,
      childAge: childAgeInYear,
      birthdayFormatErrorMessage: get(this.props, 'i18n.birthdayFormatErrorMessage'),
      adultAgeErrorMessage: get(this.props, 'i18n.adultAgeErrorMessage'),
      minAgeErrorMessage: get(this.props, 'i18n.minAgeErrorMessage'),
    });
  }

  isMobile(): boolean {
    return !get(this.props.responsive, 'isBrowserGreaterThan.S');
  }

  handleChange = <T>(action: T => *) => {
    return (payload: T) => {
      this.setState({ hasCheckedPrice: false }, () => action(payload));
    };
  };

  handleCheckPrice = () => {
    const { location } = this.props;
    const haveError = values(this.checkFormErrors()).some(error => error);

    if (!haveError) {
      return location.queryParams.hasOwnProperty('legacy')
        ? this.props.goToLegacyBookingEngine()
        : this.props.getPackages(this.props.departureDate);
    }
  };

  onSubmit = () => {
    if (this.props.isDepartureDateAvailaible) {
      this.setState({ hasCheckedPrice: true }, this.handleCheckPrice);
    }
  };

  changeTripDuration = (value: number) => {
    const { location } = this.props;
    const uri = convertLocationToURI(location);
    const searchPageUrl = uri.clone().setQuery({ [QUERY_DURATION]: value });

    redirectUtils.redirectTo()(searchPageUrl, { shallow: true });
  };

  changeDepartureCity = (value: string) => {
    const { location } = this.props;
    const uri = convertLocationToURI(location);
    const searchPageUrl = uri.clone().setQuery({ [QUERY_TRANSPORT]: value });

    redirectUtils.redirectTo()(searchPageUrl, { shallow: true });
  };

  renderNoTransport() {
    const transportVisible = get(this.props, 'i18n.transport.visible');

    return transportVisible && !get(this.props, 'isJoyView') ? (
      <div
        id="bookingOptionsTransportInfo"
        data-selector={`${this.props.dataSelector}-noTransport`}
      >
        <BlockField icon={ICONS.NO_TRANSPORT} title={get(this.props, 'i18n.transport.title')}>
          <div>{get(this.props, 'i18n.transport.description')}</div>
          <a
            data-selector="linkToLegacyResort"
            href={this.props.legacyResortRedirect}
            className="BookingForm-noTransportLink"
          >
            {get(this.props, 'i18n.transport.linkDescription')}
          </a>
        </BlockField>
      </div>
    ) : null;
  }

  renderTransport() {
    return get(this.props, 'i18n.package.transport.enableTransport', false) ? (
      <DepartureCities
        departureCities={get(this.props, 'i18n.bookingCriteria.departureCities')}
        departureCity={this.props.departureCity}
        setDepartureCity={this.changeDepartureCity}
        title={get(this.props, 'i18n.booking.form.transportFrom')}
        dataSelector="BookingOptionsDepartureCities"
      />
    ) : (
      this.renderNoTransport()
    );
  }

  renderLegalNotices() {
    return (
      <div className="BookingForm-legalNoticeWrapper">
        <div className="BookingForm-legalNotice">
          <Text
            dataSelector={`${this.props.dataSelector}-LegalNotices`}
            text={get(this.props, 'i18n.legalNotice')}
            theme="text-sans-2"
          />
        </div>
      </div>
    );
  }

  renderPushCalendarImage() {
    return !this.isMobile() ? (
      <Image
        className="w-full"
        imageUrl={this.props.pushImage}
        mandatoryWidthAndHeight={{ w: 360, h: 'auto' }}
      />
    ) : null;
  }

  renderStepTitle(number: number, title: string) {
    return (
      <h3
        className={classnames('BookingFormStep-title', `BookingFormStep-title--${number}`)}
        data-selector={`BookingCriteriaStep-title--${number}`}
      >
        <span className="BookingFormStep-titleNumber">{number}.</span>
        <span className="BookingFormStep-titleContent">{title}</span>
      </h3>
    );
  }

  renderTripCriteriaStep() {
    return (
      <div className="BookingCriteriaStep--1">
        <div className="BookingForm-durationContainer">
          <Duration
            durationOptionsFormat={get(this.props, 'i18n.durationOptionsFormat')}
            durations={get(this.props, 'i18n.bookingCriteria.durations')}
            duration={this.props.duration}
            setDuration={this.changeTripDuration}
            title={get(this.props, 'i18n.durationTitle')}
          />
          {this.renderTransport()}
          {this.renderLegalNotices()}
          {this.renderPushCalendarImage()}
        </div>
      </div>
    );
  }

  renderParticipantsStep() {
    return (
      <div className="BookingCriteriaStep--2">
        <Participants
          dateFormat={get(this.props, 'i18n.dateFormat')}
          dayPlaceholder={get(this.props, 'i18n.dayPlaceholder')}
          monthPlaceholder={get(this.props, 'i18n.monthPlaceholder')}
          yearPlaceholder={get(this.props, 'i18n.yearPlaceholder')}
          adultOnly={this.props.adultOnly}
          childrenBirthdayDateFormatPlaceholder={get(
            this.props,
            'i18n.childrenBirthdayDateFormatPlaceholder',
          )}
          setKids={this.handleChange(this.props.setKids)}
          setAdultsNumber={this.handleChange(this.props.setAdultsNumber)}
          kids={this.props.kids}
          adultsNumber={this.props.adultsNumber}
          selectAdultsLabels={get(this.props, 'i18n.selectAdultsLabels')}
          selectChildrenLabels={get(this.props, 'i18n.selectChildrenLabels')}
          minAgeInformation={get(this.props, 'i18n.minAgeInformation')}
          adultAge={get(this.props, 'i18n.adultAge')}
          childAge={get(this.props, 'childAge')}
          departureDate={get(this.props, 'departureDate')}
          kidsLabel={get(this.props, 'i18n.common.participants.kidsLabel')}
          adultsLabel={get(this.props, 'i18n.common.participants.adultsLabel')}
          kidsAgeLimit={get(this.props, 'i18n.common.participants.kidsAgeLimit')}
        />
      </div>
    );
  }

  renderSkiReassuranceNotification() {
    return this.props.isWinterResort &&
      isFeatureFlipped(this.context)('booking.reinsuranceSkiPass') ? (
      <Notification
        color={COLORS.GRENADINE}
        dataSelector="booking-criteria-reinsurance"
        title={get(this.props, 'i18n.skiPassIncludedLabel')}
        text={get(this.props, 'i18n.skiPassIncludedDescription')}
        hasBorder
      />
    ) : null;
  }

  render() {
    return (
      <div className="BookingCriteria">
        <WatchLocationQueryChange
          queryKey={QUERY_DURATION}
          onChange={value =>
            value && this.props.setDuration(parseInt(value, 10) || DEFAULT_NO_DURATION)
          }
        />
        <WatchLocationQueryChange
          queryKey={QUERY_TRANSPORT}
          onChange={value => value && this.props.setDepartureCity({ value })}
        />

        {this.renderStepTitle(1, get(this.props, 'bookingConfig.form.resortCriteriaTitle', ''))}

        <GridRow>
          <GridCell cols={COLS_M3}>{this.renderTripCriteriaStep()}</GridCell>
          {this.props.children}
        </GridRow>
        <GridRow>
          <GridCell cols={{ L: 10 }} offsets={{ L: 1 }}>
            {this.renderSkiReassuranceNotification()}
          </GridCell>
        </GridRow>
        {this.renderStepTitle(2, get(this.props, 'bookingConfig.form.guestCriteriaTitle', ''))}
        {this.renderParticipantsStep()}
        {isResort(get(this.props, 'resort.type')) && get(this.props, 'kids.length') > 0 ? (
          <GridRow>
            <GridCell cols={{ L: 10 }} offsets={{ L: 1 }}>
              <Notification
                color={COLORS.GRENADINE}
                dataSelector="booking-criteria-reinsuranceKidStay"
                title={get(this.props, 'i18n.kidStayIncludedLabel')}
                text={`**${this.props.i18n.didYouKnowLabel}** ${
                  this.props.i18n.kidStayIncludedDescription
                }`}
                hasBorder
              />
              <br />
            </GridCell>
          </GridRow>
        ) : null}
        <ParticipantsErrors
          config={pick(this.props.config.booking.form, [
            'errorMaxGuestsLabel',
            'departureDateErrorMessage',
          ])}
          errors={this.checkFormErrors()}
          isSubmitted={this.state.hasCheckedPrice}
        />
        <div className="BookingCriteria-submit">
          <Button
            iconRight={{ name: ICONS.ARROW_FORTH }}
            colorBg={COLORS.BOOKING}
            colorBorder={COLORS.BOOKING}
            onClick={this.onSubmit}
            isDisabled={this.state.isLoading}
            dataTestId="BookingCriteria-checkPrice"
            className="button--bookingForm uppercase w-full"
            size="large"
          >
            {get(this.props, 'i18n.checkPriceLabel')}
          </Button>
        </div>
      </div>
    );
  }
}

const ForwardRefBookingCriteria = ({ myRef, ...otherProps }) => (
  <BookingCriteriaComponent ref={myRef} {...otherProps} />
);

export const BookingCriteria = withRouter(ForwardRefBookingCriteria);
